import { useEffect, useMemo, useState } from 'react';
import type { LatLng, LobbyState, WSServerMsg } from '@geoguess/shared';
import type { AuthedUser } from '../discord.js';
import { RevealMap } from '../components/RevealMap.js';
import { PlayerAvatar } from '../components/PlayerAvatar.js';

export function RevealScreen({
  lobby,
  me,
  lastReveal,
}: {
  lobby: LobbyState;
  me: AuthedUser;
  lastReveal: WSServerMsg | null;
}) {
  const reveal = lastReveal && lastReveal.t === 'reveal' ? lastReveal : null;

  const truth: LatLng | null = useMemo(() => {
    if (reveal) return reveal.truth;
    if (lobby.currentImage) return { lat: lobby.currentImage.lat, lng: lobby.currentImage.lng };
    return null;
  }, [reveal, lobby.currentImage]);

  const [secsLeft, setSecsLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!reveal?.nextRoundAt) {
      setSecsLeft(null);
      return;
    }
    const tick = () =>
      setSecsLeft(Math.max(0, Math.ceil((reveal.nextRoundAt! - Date.now()) / 1000)));
    tick();
    const t = window.setInterval(tick, 250);
    return () => clearInterval(t);
  }, [reveal?.nextRoundAt]);

  const rows = useMemo(() => {
    return Object.values(lobby.players)
      .map((p) => ({
        userId: p.userId,
        name: p.name,
        avatar: p.avatar,
        score: p.score,
        roundScore: p.lastRoundScore ?? 0,
        distance: p.lastDistanceM,
      }))
      .sort((a, b) => b.score - a.score);
  }, [lobby.players]);

  return (
    <div className="grid h-full grid-cols-[1fr_360px] gap-2 overflow-hidden bg-bg p-2">
      <div className="overflow-hidden rounded-2xl ring-1 ring-border">
        {truth ? <RevealMap truth={truth} lobby={lobby} /> : null}
      </div>
      <aside className="flex flex-col gap-3 overflow-hidden rounded-2xl bg-panel p-4">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-2">
              Auflösung
            </div>
            <h2 className="text-lg font-bold tracking-tight">
              Runde {lobby.currentRound + 1} / {lobby.totalRounds}
            </h2>
          </div>
          {secsLeft !== null && (
            <span className="anim-pop inline-flex items-center gap-1.5 rounded-full bg-accent-soft px-3 py-1 text-[12px] font-semibold text-accent">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
              Nächste in {secsLeft}s
            </span>
          )}
        </div>

        <ul className="row-divide flex-1 overflow-auto rounded-xl bg-bg/30">
          {rows.map((r, idx) => {
            const place = idx + 1;
            return (
              <li
                key={r.userId}
                className="anim-slide-right flex items-center gap-3 px-3 py-2.5"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <span
                  className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${
                    place === 1
                      ? 'bg-warn text-bg'
                      : place === 2
                      ? 'bg-panel-3 text-fg'
                      : place === 3
                      ? 'bg-[#7d4f29] text-fg'
                      : 'bg-panel-2 text-muted'
                  }`}
                >
                  {place}
                </span>
                <PlayerAvatar
                  name={r.name}
                  url={r.avatar || undefined}
                  size={36}
                  ring={r.userId === me.userId}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium">{r.name}</div>
                  <div className="text-[12px] text-muted-2">
                    {r.distance != null
                      ? formatDist(r.distance)
                      : 'kein Tipp'}
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span
                    className={`font-mono text-[11px] font-semibold tabular-nums ${
                      r.roundScore > 0 ? 'text-success' : 'text-muted-2'
                    }`}
                  >
                    {r.roundScore > 0 ? `+${r.roundScore}` : '+0'}
                  </span>
                  <span className="font-mono text-[15px] font-semibold tabular-nums">
                    {r.score}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>
    </div>
  );
}

function formatDist(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  if (m < 100_000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m / 1000).toLocaleString()} km`;
}
